import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCodingRoutes } from '../../hooks/useCodingRoutes';
import {
  Alert, Box, Button, CardActionArea, CardContent,
  CircularProgress, Grid, IconButton, Typography, Chip, Stack,
} from '@mui/material';
import GlassCard from '../../components/Cards/GlassCard';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import AddBatchModal from '../../components/CodingTracker/AddBatchModal';
import useBatchStore from '../../store/batchStore';
import useStudentStore from '../../store/studentStore';
import useAssignmentStore from '../../store/assignmentStore';
import {
  surfaceGradientSx, gradientButtonSx, gradientTextSx, primaryChipSx,
  PRIMARY, SECONDARY, DANGER_BG,
} from '../../utils/colors';

const BatchListPage = () => {
  const navigate = useNavigate();
  const cr = useCodingRoutes();
  const { batches, loading, error, fetchBatches, addBatch, removeBatch } = useBatchStore();
  const { fetchStudents } = useStudentStore();
  const { fetchAssignments } = useAssignmentStore();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => { fetchBatches(false); }, [fetchBatches]);



  // Pre-fetch students and assignments for each batch
  useEffect(() => {
    batches.forEach((batch) => {
      fetchStudents(batch._id);
      fetchAssignments(batch._id);
    });
  }, [batches, fetchStudents, fetchAssignments]);

  const handleCreate = async (form) => {
    try {
      await addBatch(form);
      setShowModal(false);
    } catch (e) {
      alert(e?.response?.data?.message || e.message);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this batch and all its data?')) return;
    try {
      await removeBatch(id);
    } catch (e) {
      alert(e?.response?.data?.message || e.message);
    }
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );

  if (error)
    return <Box p={4}><Alert severity="error">Error: {error}</Alert></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto', minHeight: '100vh' }}>
      {/* ── Header ── */}
      <Box
        sx={{
          p: { xs: 2.5, md: 4 }, mb: 5, borderRadius: 4,
          ...surfaceGradientSx,
          border: '1px solid', borderColor: 'divider',
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={800} sx={gradientTextSx}>
            Coding Tracker
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Manage batches and track LeetCode progress
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineRoundedIcon />}
          onClick={() => setShowModal(true)}
          sx={{ ...gradientButtonSx, px: 3, py: 1.2 }}
        >
          Create Batch
        </Button>
      </Box>

      {/* ── Batch grid ── */}
      {batches.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 12, border: '2px dashed', borderColor: 'divider', borderRadius: 4 }}>
          <Typography variant="h6" color="text.secondary" mb={1}>No batches yet</Typography>
          <Typography variant="body2" color="text.disabled" mb={3}>
            Create your first batch to start tracking progress.
          </Typography>
          <Button variant="contained" startIcon={<AddCircleOutlineRoundedIcon />}
            onClick={() => setShowModal(true)} sx={{ ...gradientButtonSx, px: 3 }}>
            Create your first batch
          </Button>
        </Box>
      ) : (
        <Grid container spacing={4}>
          {batches.map((batch) => (
            <Grid item xs={12} sm={6} md={4} key={batch._id}>
              <GlassCard hoverable={false} sx={{ height: '100%', position: 'relative' }}>
                <CardActionArea
                  onClick={() => navigate(cr.batch(batch._id))}
                  sx={{ height: '100%', borderRadius: 3 }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <Chip label={batch.code} size="small" sx={{ ...primaryChipSx, fontSize: '0.7rem' }} />
                      <IconButton
                        size="small"
                        onClick={(e) => handleDelete(e, batch._id)}
                        sx={{ color: 'text.disabled', '&:hover': { color: 'error.main', bgcolor: DANGER_BG } }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {batch.name}
                    </Typography>
                    {batch.description && (
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {batch.description}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={2} mt={2}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <GroupsRoundedIcon fontSize="small" sx={{ color: PRIMARY }} />
                        <Box>
                          <Typography variant="h6" fontWeight={700} lineHeight={1}>
                            {batch.studentCount ?? '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Students
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <AssignmentRoundedIcon fontSize="small" sx={{ color: SECONDARY }} />
                        <Box>
                          <Typography variant="h6" fontWeight={700} lineHeight={1}>
                            {batch.assignmentCount ?? '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Assignments
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                    <Typography
                      variant="body2"
                      mt={2.5}
                      sx={{ color: PRIMARY, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      View Details
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </GlassCard>
            </Grid>
          ))}
        </Grid>
      )}

      {showModal && <AddBatchModal onClose={() => setShowModal(false)} onSubmit={handleCreate} />}
    </Box>
  );
};

export default BatchListPage;
